import React, { useState, ReactNode, useEffect } from 'react';
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  Button,
  Typography,
  Divider,
  Container,
  Paper,
  IconButton,
  Tooltip,
  useMediaQuery
} from '@mui/material';
// Use relative import without extension
import SidebarMenu from './SidebarMenu';
import AliphiaHeader from './AliphiaHeader';
import {
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

interface DashboardLayoutProps {
  children?: ReactNode;
}

// Fixed drawer width
const DRAWER_WIDTH = 240;

// Main content area that adjusts based on drawer being open or closed
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  paddingTop: theme.spacing(9),
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // على الشاشات الكبيرة، نحسب العرض بناءً على حالة القائمة
  [theme.breakpoints.up('md')]: {
    width: `calc(100% - ${open ? DRAWER_WIDTH : 0}px)`,
    marginLeft: open ? DRAWER_WIDTH : 0,
    paddingTop: theme.spacing(9),
  },
  // على الشاشات الصغيرة، نستخدم العرض الكامل دائماً + مساحة إضافية للرسالة
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 0,
    paddingTop: theme.spacing(12), // مساحة إضافية للرسالة العلوية على الجوال
  },
  direction: 'ltr',
  backgroundColor: '#f8f9fa',
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
  height: 64,
}));

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  
  // تحديث حالة القائمة عند تغيير حجم الشاشة
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);
  
  const handleToggleSidebar = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', direction: 'ltr', minHeight: '100vh' }}>
      <AliphiaHeader />
      
      {/* Toggle button - يظهر فقط عندما تكون القائمة مغلقة */}
      {!open && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 100, md: 72 }, // مساحة إضافية على الجوال للرسالة العلوية
            left: 10,
            zIndex: 1000,
            backgroundColor: 'white',
            borderRadius: '50%',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          <IconButton
            onClick={handleToggleSidebar}
            sx={{
              color: '#30c3b0',
              '&:hover': { bgcolor: 'rgba(48, 195, 176, 0.08)' }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}
      
      <Main open={open}>
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 1, sm: 2, md: 3 },
            maxWidth: '100%',
            mx: 'auto'
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {children}
          </Box>
        </Container>
      </Main>

      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 'none',
            boxShadow: '1px 0 15px rgba(0,0,0,0.08)',
            overflow: 'auto'
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // أفضل أداء على الجوال
        }}
      >
        <DrawerHeader>
          <Tooltip title="إغلاق القائمة" arrow>
            <IconButton onClick={handleToggleSidebar} size="small">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DrawerHeader>
        
        <Divider sx={{ opacity: 0.6, mb: 1 }} />
        <SidebarMenu />
      </Drawer>
    </Box>
  );
};

export default DashboardLayout;